import { InlineAlertType } from '@tableau/widgets';

import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { DomainID } from '~src/settings/bridge-settings/sections/types/Domain';
import { DomainUpdateType } from '~src/settings/bridge-settings/serverApi/requests/UpdateEdgePoolRequest';
import { IGetSmbVolumeDetailsResult } from '~src/settings/dataconnect-settings/serverApi/requests/GetSmbVolumeDetailsParams';
import { ISetSmbVolumesResult } from '~src/settings/dataconnect-settings/serverApi/requests/SetSmbVolumesParams';
import { IUpdateBaasEdgePoolResult } from '~src/settings/dataconnect-settings/serverApi/requests/UpdateBaasEdgePoolRequest';
import { BaasEdgePoolAndCluster } from '~src/settings/dataconnect-settings/serverApi/resources/BaasEdgePoolAndCluster';

import { ActionItemInfo } from '../../MoreActionsMenu';
import { DomainPermission } from '../../types/DomainPermission';
import { DomainTableItem } from '../../types/DomainTableItem';
import { DomainType } from '../../types/DomainType';
import { DomainValidationStatus } from '../../types/DomainValidationStatus';
import { FilePathValidationStatus } from '../../types/FilePathValidationStatus';
import { PoolID } from '../../types/PoolID';
import { createDomainTableItems } from '../../utils/DomainUtilities';
import { getGeneralErrorMessage } from '../../utils/EdgePoolErrorUtilities';
import { AddNewDomainDialog, AddNewDomainDialogInputProps, AddNewDomainDialogState } from '../addNewDomainDialog/AddNewDomainDialog';

export function openEditDomainDialog(
    domainEditableProperties: { domainType: DomainType,domainName: string, domainPermission: DomainPermission, filePath: string},
    onEditDomainDialogSave: (domainName: string, domainPermission: DomainPermission, domainType: DomainType, fileServer: string, username: string, password: string, oldPassword: string) => Promise<void>,
    onTestConnection: (filePath: string, username: string, password: string, oldPassword: string) => Promise<{ message: string, messageType: InlineAlertType }>,
    otherDomainNames: ReadonlyArray<string>,
    poolDomainNames: ReadonlyArray<string>,
    poolID: PoolID,
    poolName: string
) {
    const action = (_props: AddNewDomainDialogInputProps, state: AddNewDomainDialogState) => {
        return onEditDomainDialogSave(state.domainName, state.domainPermission, state.domainType, state.filePath, state.username, state.password, state.oldPassword);
    };
    const onTestConnectionAction =  (state: AddNewDomainDialogState)   => {
      return onTestConnection(state.filePath, state.username, state.password, state.oldPassword);
    };

    const loadFileServerDetails = (state: AddNewDomainDialogState): Promise<AddNewDomainDialogState> => {
        return BaasEdgePoolAndCluster.getSmbVolumeDetails({path: '//' + state.filePath, poolId: poolID})
                            .then((result: IGetSmbVolumeDetailsResult) => {
                              if (result.success) {
                                return Promise.resolve({
                                  ...state,
                                  username: result.success.username,
                                  oldPassword: result.success.password
                                });
                              }
                              ToasterService.instance.pop({
                                type: ToastType.Error,
                                text: getGeneralErrorMessage(result.errors[0].code, result.error.errorMessage)
                              });
                              throw new Error(getGeneralErrorMessage(result.errors[0].code, result.error.errorMessage));
                            })
                            .catch((error: Error) => {
                              ToasterService.instance.pop({
                                type: ToastType.Error,
                                text: error.message
                              });
                              throw new Error(error.message);
                            });
    };
    const dialogInstance = openReactActionDialog({
        title: domainEditableProperties.domainType === DomainType.FILESERVER ? Messages.dataConnectSiteSettingsEditDomainFileServer({POOL_NAME: poolName}) : Messages.dataConnectSiteSettingsEditDomain({POOL_NAME: poolName}),
        bodyComponent: AddNewDomainDialog,
        initialBodyProps: { poolID, poolName, otherDomainNames, poolDomainNames, onTestConnection: onTestConnectionAction, isEditDomainDialog: true},
        initialDialogState: {
          ...domainEditableProperties,
          existingDomainName: domainEditableProperties.domainName,
          existingFilePath: domainEditableProperties.filePath,
          domainValidationStatus: DomainValidationStatus.NO_ERROR,
          filePathValidationStatus: FilePathValidationStatus.NO_ERROR,
          username: '',
          password: '',
          oldPassword: ''
        },
        fetchInitialState: (bodyProps: AddNewDomainDialogInputProps, dialogState: AddNewDomainDialogState) => {
          if (dialogState.domainType === DomainType.FILESERVER) {
            return loadFileServerDetails(dialogState);
          }
          return Promise.resolve(dialogState);
        },
        actionDisabled: (_props, state) => (state.domainType === DomainType.FILESERVER && (state.filePath === '' || state.domainValidationStatus !== DomainValidationStatus.NO_ERROR))
        || (state.domainType !== DomainType.FILESERVER && (state.domainName === '' || state.domainValidationStatus !== DomainValidationStatus.NO_ERROR)),
        actionName: Messages.save(),
        action: action,
        isActionDelete: false,
        dialogWidth: TabDialogWidth.width500px,
        dismissDialogOnActionFailed: false
    });

    return Promise.resolve(dialogInstance.result);
}

export function openEditPoolDomainDialog(actionItem: ActionItemInfo) {

    const onEditDomainDialogSave = (domainName: string, domainPermission: DomainPermission,
      domainType: DomainType, fileServer: string, username: string, password: string, oldPassword: string): Promise<void> => {

        let domainNameToUse = domainName;
        if (domainType === DomainType.FILESERVER) {
          const domain = fileServer.match(/^([A-Za-z0-9\-_.]+)[\\/]/);
          domainNameToUse = domain ? domain[1] : fileServer;
        }

        return BaasEdgePoolAndCluster.updateBaasPool({
        poolId: actionItem.poolId,
        displayName: null,
        domainChange: { change: DomainUpdateType.Remove, domainId: actionItem.domain.domainName as DomainID },
        DataConnectSettingsVersion: '0'
      }).then((resultForRemove: IUpdateBaasEdgePoolResult) => {
        if (resultForRemove.success) {
          return BaasEdgePoolAndCluster.updateBaasPool({
            poolId: actionItem.poolId,
            displayName: null,
            domainChange: { change: DomainUpdateType.Add, domainId: domainNameToUse as DomainID },
            DataConnectSettingsVersion: '0'
          }).then((resultForAdd: IUpdateBaasEdgePoolResult) => {
            if (resultForAdd.success) {
              // file server case add the file server config
              if (domainType === DomainType.FILESERVER) {
                // Convert subfolders to full paths if this is a file server
                let fullPath: string = '//' + fileServer;

                // Extract domain from username (format: domain\username, domain/username)
                let extractedDomain = '';
                let extractedUsername = username;
                if (username) {
                  // Check for domain\username or domain/username format
                  const domainMatch = username.match(/^([^\\\/]+)[\\\/](.+)$/);
                  if (domainMatch && domainMatch[1]) {
                    extractedDomain = domainMatch[1];
                    extractedUsername = domainMatch[2];
                  }
                }

                return BaasEdgePoolAndCluster.setSmbVolumes({
                  poolId: actionItem.poolId,
                  path: fullPath,
                  domain: extractedDomain,
                  username: extractedUsername,
                  password: password === '' ? oldPassword : password,
                  dryRun: false
                }).then((result: ISetSmbVolumesResult) => {
                  if (result.success) {
                    actionItem.refreshPoolsAndClusters();
                    ToasterService.instance.pop({ type: ToastType.Success, text: Messages.dataConnectSiteSettingsEditDomainFileServerSuccess() });
                  }
                  if (result.errors) {
                    ToasterService.instance.pop({
                      type: ToastType.Error,
                      text: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message)
                    });
                    throw new Error(getGeneralErrorMessage(resultForAdd.errors[0].code, resultForAdd.errors[0].message));
                  }
                  return Promise.resolve();
                });
              } else {
                actionItem.refreshPoolsAndClusters();
                ToasterService.instance.pop({ type: ToastType.Success, text: Messages.dataConnectSiteSettingsEditDomainSuccess() });
              }
            }
            if (resultForAdd.errors) {
              ToasterService.instance.pop({
                type: ToastType.Error,
                text: getGeneralErrorMessage(resultForAdd.errors[0].code, resultForAdd.errors[0].message)
              });
              throw new Error(getGeneralErrorMessage(resultForAdd.errors[0].code, resultForAdd.errors[0].message));
            }
            return Promise.resolve();
          });
        }

        if (resultForRemove.errors) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getGeneralErrorMessage(resultForRemove.errors[0].code, resultForRemove.errors[0].message)
          });
          throw new Error(getGeneralErrorMessage(resultForRemove.errors[0].code, resultForRemove.errors[0].message));
        }
        return Promise.resolve();
      });
      };
      const onTestConnection = (fileServer: string, username: string, password: string, oldPassword: string) => {

        let fullPath: string = '//'+fileServer;

        // Extract domain from username (format: domain\username, domain/username)
        let extractedDomain = '';
        let extractedUsername = username;
        if (username) {
          // Check for domain\username or domain/username format
          const domainMatch = username.match(/^([^\\\/]+)[\\\/](.+)$/);
          if (domainMatch && domainMatch[1]) {
            extractedDomain = domainMatch[1];
            extractedUsername = domainMatch[2];
          }
        }

        return BaasEdgePoolAndCluster.setSmbVolumes({
          poolId: actionItem.poolId,
          path: fullPath,
          domain: extractedDomain,
          username: extractedUsername,
          password: password === '' ? oldPassword : password,
          dryRun: true
        })
          .then((result: ISetSmbVolumesResult) => {
            if (result.success) {
              return { message: Messages.dataConnectSiteSettingsDomainFileServerTestConnectionSuccess(), messageType: InlineAlertType.Success };
            }
            if (result.errors) {
              return { message: getGeneralErrorMessage(result.errors[0].code, result.errors[0].message), messageType: InlineAlertType.Error };
            }
            return { message: Messages.dataConnectSiteSettingsDomainFileServerTestConnection(), messageType: InlineAlertType.Error };
          });
      };

      const filePath = actionItem.filePath.replace('//', '');
      const domainEditableProperties = {
        domainName: actionItem.domain.domainName,
        poolID: actionItem.poolId,
        poolName: actionItem.poolName,
        domainPermission: DomainPermission.ALLOW,
        domainType: actionItem.domainType,
        filePath: filePath
      };
      const domainTableItems: ReadonlyArray<DomainTableItem> = createDomainTableItems(actionItem.userDefinedPools, {});
      const bridgeDomainNames: ReadonlyArray<string> = Object.keys(actionItem.bridgeDomains);

      openEditDomainDialog(domainEditableProperties, onEditDomainDialogSave, onTestConnection,
        domainTableItems.map((item: DomainTableItem) => item.domainName), bridgeDomainNames, actionItem.poolId, actionItem.poolName);
}
